import React, {useState, useEffect} from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
} from '@mui/material';
import { Add, Save } from '@material-ui/icons';
import { replaceSpacesWithUnderscores } from "../../../../lib/utils.es6";
import { toCents } from "../../moneyUtils";

const PackageForm = ({handleAddPackage, initialData, isEditMode, currency}) => {

   console.log("PackageForm props:", { initialData, isEditMode });

   const [packageData, setPackageData] = useState({
    name: "",
    code: "",
    premium_granularity: "Month",
    premium_cardinality: "",
    premium_amount: "",
  })

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    console.log("PackageForm useEffect triggered:", { isEditMode, initialData });
    if (isEditMode && initialData) {
      console.log("Setting form to edit mode with data:", initialData);
      setPackageData({
        name: initialData.name || "",
        code: initialData.code || "",
        premium_granularity: initialData.premium_granularity || "Month",
        premium_cardinality: initialData.premium_cardinality || "",
        premium_amount: "",
      });
    } else {
      console.log("Setting form to create mode - resetting form");
      setPackageData({
        name: "",
        code: "",
        premium_granularity: "Month",
        premium_cardinality: "",
        premium_amount: "",
      });
    }
    setErrors({});
    setTouched({});
  }, [isEditMode, initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!packageData.name || !packageData.name.trim()) {
      newErrors.name = "Package name is required";
    } else if (packageData.name.trim().length < 2) {
      newErrors.name = "Package name must be at least 2 characters";
    }

    if (!packageData.code || !packageData.code.trim()) {
      newErrors.code = "Package code is required";
    }

    if (packageData.premium_amount !== "" && packageData.premium_amount !== null) {
      const num = Number(packageData.premium_amount);
      if (isNaN(num) || num < 0) {
        newErrors.premium_amount = "Premium amount must be a non-negative number";
      }
    }

    if (packageData.premium_cardinality !== "" && packageData.premium_cardinality !== null) {
      if (parseInt(packageData.premium_cardinality) <= 0) {
        newErrors.premium_cardinality = "Premium cardinality must be greater than 0";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFieldError = (fieldName) => {
    return touched[fieldName] && errors[fieldName] ? errors[fieldName] : "";
  };

  const hasFieldError = (fieldName) => {
    return touched[fieldName] && Boolean(errors[fieldName]);
  };

  const handlePackageInputChange = (field) => (event) => {
    setTouched(prev => ({ ...prev, [field]: true }));

    if (field === "name") {
      const generatedCode = replaceSpacesWithUnderscores(event.target.value.toLowerCase().trim());
      setPackageData({
        ...packageData,
        name: event.target.value,
        code: generatedCode,
      });
      if (errors.name || errors.code) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.name;
          delete newErrors.code;
          return newErrors;
        });
      }
      return;
    }

    const value = event.target.value;
    setPackageData({
      ...packageData,
      [field]: value,
    });

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleAddPackageSubmit = (event) => {
    event.preventDefault();

    const allFields = ['name', 'code'];
    const touchedState = {};
    allFields.forEach(field => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    if (!validateForm()) {
      return;
    }

    const payload = {
      name: packageData.name,
      code: packageData.code,
      premium_granularity: packageData.premium_granularity,
    };

    if (packageData.premium_cardinality !== "" && packageData.premium_cardinality !== null) {
      payload.premium_cardinality = parseInt(packageData.premium_cardinality) || 1;
    }

    // Create-only: the saga turns this into a flat_rate premium RatingNode and
    // links it, since PackageDto has no premium_amount_in_cents field of its
    // own. Editing an existing package's premium happens through its Premium
    // Rating Node picker, not here.
    if (!isEditMode && packageData.premium_amount !== "" && packageData.premium_amount !== null) {
      payload.premium_amount_in_cents = toCents(packageData.premium_amount);
    }

    console.log("Submitting Package Data from form:", payload);
    handleAddPackage(payload);
  };

  const moneyInputProps = currency ? {
    startAdornment: (
      <InputAdornment position="start">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{currency}</Typography>
      </InputAdornment>
    )
  } : {};

  return (
    <>
      <Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Package Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Gold Package"
              value={packageData.name || ""}
              onChange={handlePackageInputChange("name")}
              variant="outlined"
              error={hasFieldError("name")}
              helperText={getFieldError("name")}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Package Code
            </Typography>
            <TextField
              fullWidth
              placeholder="gold_pkg"
              value={packageData.code || ""}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Package Premium Amount
            </Typography>
            {isEditMode ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                Edit the premium via this package's Premium Rating Node, below.
              </Typography>
            ) : (
              <TextField
                fullWidth
                type="number"
                placeholder="0.00"
                value={packageData.premium_amount}
                onChange={handlePackageInputChange("premium_amount")}
                variant="outlined"
                error={hasFieldError("premium_amount")}
                helperText={getFieldError("premium_amount")}
                InputProps={moneyInputProps}
              />
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Granularity
            </Typography>
            <FormControl fullWidth variant="outlined">
              <Select
                value={packageData.premium_granularity}
                onChange={handlePackageInputChange("premium_granularity")}
              >
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="1"
              value={packageData.premium_cardinality}
              onChange={handlePackageInputChange("premium_cardinality")}
              variant="outlined"
              error={hasFieldError("premium_cardinality")}
              helperText={getFieldError("premium_cardinality")}
            />
          </Box>
        </Box>

        <Button
          fullWidth
          variant="contained"
          startIcon={isEditMode ? <Save /> : <Add />}
          sx={{
            textTransform: "none",
            backgroundColor: "#fba92c",
            "&:hover": {
              backgroundColor: "#f59e0b",
            },
            py: 1.5,
            mt: 1,
          }}
          onClick={handleAddPackageSubmit}
        >
          {isEditMode ? "Save Changes" : "Add Package"}
        </Button>
      </Box>
    </>
  );
};
export default PackageForm;
